'use client';

import { c as _c } from "react/compiler-runtime";
import './gift-catalog-core.scss';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ElementType, Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { Prominence, Sizes, Text, Variants } from '@sola/ark-core/atoms/text/text.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { ChipGroup } from '@sola/ark-core/molecules/chip-group/chip-group.tsx';
import { Form } from '@sola/ark-core/molecules/form/form.tsx';
import { Select, SelectItemProps } from '@sola/ark-core/molecules/select/select.tsx';
import { SliderInput } from '@sola/ark-core/molecules/slider-input/slider-input.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { generalConfig } from '@sola/config/lib/getters.ts';
import { BlockProps, FundProps } from '@sola/schema/lib/schema.ts';
import { extractArgsSearchResult } from '@sola/skippy-search-sdk/lib/logic.ts';
import { useListCatalogQuery } from '@sola/skippy-search-sdk/lib/queries.ts';
import { SearchResultProps } from '@sola/skippy-search-sdk/lib/schema.ts';
import { TrackViewFundItemList, trackClick, trackSelectFundItem } from '@sola/tracking/components/datalayer.tsx';
import { GiftCatalogCard } from '../../../molecules/gift-catalog-card/gift-catalog-card.tsx';
import { HeaderGroup } from '../../../molecules/header-group/header-group.tsx';
import { GiftCatalogSortEnum, useGiftCatalogQueryParams } from '../../../utils/query-params.tsx';
import { GiftCatalogCoreCardsSkeleton, GiftCatalogCoreSkeleton } from './gift-catalog-core-skeleton.tsx';
const queryClient = new QueryClient();
const InternalGiftCatalogCore: React.FC<BlockProps> = t0 => {
  const $ = _c(225);
  const {
    seoLevel: t1
  } = t0;
  const seoLevel = t1 === undefined ? 2 : t1;
  const [isCategoryOpen, setIsCategoryOpen] = useState(true);
  const [isPriceOpen, setIsPriceOpen] = useState(true);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [allResults, setAllResults] = useState(t2);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["gift-catalog", "skippy"];
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    t
  } = useTranslation(t3);
  const {
    giftCatalogFilters,
    setGiftCatalogFilters
  } = useGiftCatalogQueryParams();
  const selectedCategoryFilters = giftCatalogFilters.category;
  const selectedPromotion = giftCatalogFilters.promotion;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [generalConfig.catalogFilterMin, generalConfig.catalogFilterMax];
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const [priceRange, setPriceRange] = useState(t4);
  let t5;
  let t6;
  if ($[3] !== giftCatalogFilters.priceMax || $[4] !== giftCatalogFilters.priceMin) {
    t5 = () => {
      const minPrice = giftCatalogFilters.priceMin;
      const maxPrice = giftCatalogFilters.priceMax;
      setPriceRange([minPrice ?? generalConfig.catalogFilterMin, maxPrice ?? generalConfig.catalogFilterMax]);
    };
    t6 = [giftCatalogFilters.priceMin, giftCatalogFilters.priceMax];
    $[3] = giftCatalogFilters.priceMax;
    $[4] = giftCatalogFilters.priceMin;
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  useEffect(t5, t6);
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setCurrentPage(1);
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== giftCatalogFilters.category || $[9] !== giftCatalogFilters.priceMax || $[10] !== giftCatalogFilters.priceMin || $[11] !== giftCatalogFilters.sortby) {
    t8 = [giftCatalogFilters.category, giftCatalogFilters.priceMin, giftCatalogFilters.priceMax, giftCatalogFilters.sortby];
    $[8] = giftCatalogFilters.category;
    $[9] = giftCatalogFilters.priceMax;
    $[10] = giftCatalogFilters.priceMin;
    $[11] = giftCatalogFilters.sortby;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  useEffect(t7, t8);
  const categoryLabels = t("gift-catalog:taxonomy.fundCategories", {
    returnObjects: true
  }) as Record<string, string>;
  let t9;
  if ($[13] !== t) {
    t9 = t("gift-catalog:sort.mostNeeded");
    $[13] = t;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t9) {
    t10 = {
      value: GiftCatalogSortEnum.enum.mostNeeded,
      text: t9
    };
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t) {
    t11 = t("gift-catalog:sort.priceLowToHigh");
    $[17] = t;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11) {
    t12 = {
      value: GiftCatalogSortEnum.enum.priceLowToHigh,
      text: t11
    };
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t) {
    t13 = t("gift-catalog:sort.priceHighToLow");
    $[21] = t;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t13) {
    t14 = {
      value: GiftCatalogSortEnum.enum.priceHighToLow,
      text: t13
    };
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t) {
    t15 = t("gift-catalog:sort.alphabeticalAZ");
    $[25] = t;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t15) {
    t16 = {
      value: GiftCatalogSortEnum.enum.alphabeticalAZ,
      text: t15
    };
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t) {
    t17 = t("gift-catalog:sort.alphabeticalZA");
    $[29] = t;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t17) {
    t18 = {
      value: GiftCatalogSortEnum.enum.alphabeticalZA,
      text: t17
    };
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t10 || $[34] !== t12 || $[35] !== t14 || $[36] !== t16 || $[37] !== t18) {
    t19 = [t10, t12, t14, t16, t18];
    $[33] = t10;
    $[34] = t12;
    $[35] = t14;
    $[36] = t16;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const sortOptions = t19;
  const getSearchSort = _temp;
  let sortConfig;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  if ($[39] !== currentPage || $[40] !== giftCatalogFilters.priceMax || $[41] !== giftCatalogFilters.priceMin || $[42] !== giftCatalogFilters.sortby || $[43] !== selectedCategoryFilters || $[44] !== selectedPromotion) {
    const filters = {};
    const should = {};
    const terms = {};
    sortConfig = getSearchSort(giftCatalogFilters.sortby as GiftCatalogSortEnum);
    if (selectedCategoryFilters.length > 0) {
      terms["taxonomy.fundCategory.keyword"] = selectedCategoryFilters;
    }
    const promotionFilter = selectedPromotion || sortConfig.should;
    if (promotionFilter) {
      should["taxonomy.fundPromotion.keyword"] = promotionFilter;
    }
    const ranges = {};
    const minPrice_0 = giftCatalogFilters.priceMin ?? generalConfig.catalogFilterMin;
    const maxPrice_0 = giftCatalogFilters.priceMax ?? generalConfig.catalogFilterMax;
    ranges["fund.catalogAmount"] = {
      gte: minPrice_0,
      lte: maxPrice_0
    };
    t20 = (currentPage - 1) * generalConfig.catalogPerPage;
    t21 = generalConfig.catalogPerPage;
    t22 = JSON.stringify(filters);
    t23 = JSON.stringify(terms);
    t24 = JSON.stringify(ranges);
    t25 = JSON.stringify(should);
    $[39] = currentPage;
    $[40] = giftCatalogFilters.priceMax;
    $[41] = giftCatalogFilters.priceMin;
    $[42] = giftCatalogFilters.sortby;
    $[43] = selectedCategoryFilters;
    $[44] = selectedPromotion;
    $[45] = sortConfig;
    $[46] = t20;
    $[47] = t21;
    $[48] = t22;
    $[49] = t23;
    $[50] = t24;
    $[51] = t25;
  } else {
    sortConfig = $[45];
    t20 = $[46];
    t21 = $[47];
    t22 = $[48];
    t23 = $[49];
    t24 = $[50];
    t25 = $[51];
  }
  let t26;
  if ($[52] !== sortConfig.sort || $[53] !== sortConfig.sortDir || $[54] !== t20 || $[55] !== t21 || $[56] !== t22 || $[57] !== t23 || $[58] !== t24 || $[59] !== t25) {
    t26 = {
      offset: t20,
      perPage: t21,
      filters: t22,
      terms: t23,
      ranges: t24,
      should: t25,
      shouldStrictness: 0,
      sort: sortConfig.sort,
      sortDir: sortConfig.sortDir
    };
    $[52] = sortConfig.sort;
    $[53] = sortConfig.sortDir;
    $[54] = t20;
    $[55] = t21;
    $[56] = t22;
    $[57] = t23;
    $[58] = t24;
    $[59] = t25;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  const searchInput = t26;
  const {
    data: searchResults,
    isLoading,
    error
  } = useListCatalogQuery(searchInput);
  let t27;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = {
      perPage: 1
    };
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  const catalogFacetQuery = useListCatalogQuery(t27);
  const {
    data: categoryData
  } = catalogFacetQuery;
  let t28;
  if ($[62] !== categoryData?.facets || $[63] !== t) {
    const facetCategories = categoryData?.facets.filter(_temp2) || [];
    t28 = (facetCategories[0]?.values.map(category => ({
      name: t(`gift-catalog:taxonomy.fundCategories.${category.key}`),
      value: category.key
    })) || []).sort(_temp3);
    $[62] = categoryData?.facets;
    $[63] = t;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  const categories = t28;
  let t29;
  if ($[65] !== setGiftCatalogFilters) {
    t29 = sliderRange => {
      const newMin = sliderRange.newValue[0];
      const newMax = sliderRange.newValue[1];
      setGiftCatalogFilters(prev => ({
        ...prev,
        priceMin: newMin !== generalConfig.catalogFilterMin ? newMin : null,
        priceMax: newMax !== generalConfig.catalogFilterMax ? newMax : null
      }));
    };
    $[65] = setGiftCatalogFilters;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  const handlePriceChange = t29;
  let t30;
  if ($[67] !== setGiftCatalogFilters) {
    t30 = selected => {
      setGiftCatalogFilters(prev_0 => ({
        ...prev_0,
        category: selected
      }));
    };
    $[67] = setGiftCatalogFilters;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  const handleActiveFilterChange = t30;
  let t31;
  if ($[69] !== setGiftCatalogFilters) {
    t31 = newSort => {
      setGiftCatalogFilters(prev_1 => ({
        ...prev_1,
        sortby: newSort
      }));
    };
    $[69] = setGiftCatalogFilters;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  const handleSortChange = t31;
  let t32;
  let t33;
  if ($[71] !== currentPage || $[72] !== searchResults) {
    t32 = () => {
      if (!searchResults?.items) {
        return;
      }
      const newResults = searchResults.items.map(extractArgsSearchResult);
      if (currentPage === 1) {
        setAllResults(newResults);
      } else {
        setAllResults(prev_2 => [...prev_2, ...newResults]);
      }
    };
    t33 = [searchResults, currentPage];
    $[71] = currentPage;
    $[72] = searchResults;
    $[73] = t32;
    $[74] = t33;
  } else {
    t32 = $[73];
    t33 = $[74];
  }
  useEffect(t32, t33);
  const Comp2 = `h${seoLevel + 1}` as ElementType;
  const Comp3 = `h${seoLevel + 2}` as ElementType;
  const hasMore = searchResults?.totalCount && searchResults.totalCount > currentPage * generalConfig.catalogPerPage;
  let t34;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = () => {
      setCurrentPage(_temp4);
    };
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  const handleLoadMore = t34;
  let t35;
  if ($[76] !== setGiftCatalogFilters || $[77] !== t) {
    t35 = () => {
      trackClick(t("gift-catalog:clearAll"));
      setGiftCatalogFilters(null);
    };
    $[76] = setGiftCatalogFilters;
    $[77] = t;
    $[78] = t35;
  } else {
    t35 = $[78];
  }
  const resetAllFilters = t35;
  let t36;
  if ($[79] !== setGiftCatalogFilters || $[80] !== t) {
    t36 = () => {
      trackClick(t("gift-catalog:clearPriceRange"));
      setGiftCatalogFilters(_temp5);
    };
    $[79] = setGiftCatalogFilters;
    $[80] = t;
    $[81] = t36;
  } else {
    t36 = $[81];
  }
  const resetPriceFilter = t36;
  const isPriceFiltered = giftCatalogFilters.priceMin !== null && giftCatalogFilters.priceMin !== generalConfig.catalogFilterMin || giftCatalogFilters.priceMax !== null && giftCatalogFilters.priceMax !== generalConfig.catalogFilterMax;
  if (error) {
    let t37;
    if ($[82] !== t) {
      t37 = t("gift-catalog:error");
      $[82] = t;
      $[83] = t37;
    } else {
      t37 = $[83];
    }
    let t38;
    if ($[84] !== t37) {
      t38 = <section className="GiftCatalogCore component"><div className="container"><Alert variant="negative" message={t37} /></div></section>;
      $[84] = t37;
      $[85] = t38;
    } else {
      t38 = $[85];
    }
    return t38;
  }
  const t37 = "GiftCatalogCore component";
  const t38 = "GiftCatalogCore__container container grid gap-comfy-sm md:gap-comfy-xl";
  let t39;
  if ($[86] !== t) {
    t39 = t("gift-catalog:viewAllGifts");
    $[86] = t;
    $[87] = t39;
  } else {
    t39 = $[87];
  }
  let t40;
  if ($[88] !== t) {
    t40 = t("gift-catalog:subHeading");
    $[88] = t;
    $[89] = t40;
  } else {
    t40 = $[89];
  }
  let t41;
  if ($[90] !== t40) {
    t41 = <p>{t40}</p>;
    $[90] = t40;
    $[91] = t41;
  } else {
    t41 = $[91];
  }
  let t42;
  if ($[92] !== seoLevel || $[93] !== t39 || $[94] !== t41) {
    t42 = <div className="flex-1 "><HeaderGroup seoLevel={seoLevel} title={t39} body={t41} /></div>;
    $[92] = seoLevel;
    $[93] = t39;
    $[94] = t41;
    $[95] = t42;
  } else {
    t42 = $[95];
  }
  let t43;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <ArkIcon iconName="settings-04" />;
    $[96] = t43;
  } else {
    t43 = $[96];
  }
  let t44;
  if ($[97] !== isMobileFiltersOpen) {
    t44 = () => setIsMobileFiltersOpen(!isMobileFiltersOpen);
    $[97] = isMobileFiltersOpen;
    $[98] = t44;
  } else {
    t44 = $[98];
  }
  let t45;
  if ($[99] !== selectedCategoryFilters.length || $[100] !== t) {
    t45 = selectedCategoryFilters.length > 0 ? t("gift-catalog:filterBy", {
      count: selectedCategoryFilters.length
    }) : t("gift-catalog:filterByWithoutCount");
    $[99] = selectedCategoryFilters.length;
    $[100] = t;
    $[101] = t45;
  } else {
    t45 = $[101];
  }
  let t46;
  if ($[102] !== t44 || $[103] !== t45) {
    t46 = <div className="GiftCatalogCore__filterToggle md:hidden flex-1"><ButtonStyle variant="primary" size="md" iconAfter={t43}><button type="button" className="justify-between w-full" onClick={t44}>{t45}</button></ButtonStyle></div>;
    $[102] = t44;
    $[103] = t45;
    $[104] = t46;
  } else {
    t46 = $[104];
  }
  let t47;
  if ($[105] !== t) {
    t47 = t("gift-catalog:sortBy");
    $[105] = t;
    $[106] = t47;
  } else {
    t47 = $[106];
  }
  let t48;
  if ($[107] !== Comp2 || $[108] !== t47) {
    t48 = <div className="hidden md:block w-full"><Text variant={Variants.Label} size={Sizes.Medium} asChild={true}><Comp2>{t47}</Comp2></Text></div>;
    $[107] = Comp2;
    $[108] = t47;
    $[109] = t48;
  } else {
    t48 = $[109];
  }
  const t49 = giftCatalogFilters.sortby;
  let t50;
  if ($[110] !== handleSortChange) {
    t50 = e => handleSortChange(e.target.value);
    $[110] = handleSortChange;
    $[111] = t50;
  } else {
    t50 = $[111];
  }
  let t51;
  if ($[112] !== t) {
    t51 = t("gift-catalog:sortGiftsBy");
    $[112] = t;
    $[113] = t51;
  } else {
    t51 = $[113];
  }
  let t52;
  if ($[114] !== giftCatalogFilters.sortby || $[115] !== sortOptions || $[116] !== t50 || $[117] !== t51) {
    t52 = <Select name="sort" items={sortOptions} defaultValue={t49} onChange={t50} aria-label={t51} />;
    $[114] = giftCatalogFilters.sortby;
    $[115] = sortOptions;
    $[116] = t50;
    $[117] = t51;
    $[118] = t52;
  } else {
    t52 = $[118];
  }
  let t53;
  if ($[119] !== t48 || $[120] !== t52) {
    t53 = <div className="GiftCatalogCore__sort grid gap-snug-lg relative flex-1">{t48}{t52}</div>;
    $[119] = t48;
    $[120] = t52;
    $[121] = t53;
  } else {
    t53 = $[121];
  }
  let t54;
  if ($[122] !== t46 || $[123] !== t53) {
    t54 = <div className="flex gap-comfy-sm">{t46}{t53}</div>;
    $[122] = t46;
    $[123] = t53;
    $[124] = t54;
  } else {
    t54 = $[124];
  }
  let t55;
  if ($[125] !== t42 || $[126] !== t54) {
    t55 = <div className="GiftCatalogCore__header flex flex-col gap-comfy-md md:items-end md:flex-row ">{t42}{t54}</div>;
    $[125] = t42;
    $[126] = t54;
    $[127] = t55;
  } else {
    t55 = $[127];
  }
  const t56 = "GiftCatalogCore__body grid items-start gap-comfy-lg";
  const t57 = !isMobileFiltersOpen;
  let t58;
  if ($[128] !== isMobileFiltersOpen || $[129] !== t57) {
    t58 = cn("GiftCatalogCore__filters grid px-comfy-sm py-comfy-lg rounded-lg", "md:grid", {
      hidden: t57,
      grid: isMobileFiltersOpen
    });
    $[128] = isMobileFiltersOpen;
    $[129] = t57;
    $[130] = t58;
  } else {
    t58 = $[130];
  }
  let t59;
  if ($[131] !== selectedCategoryFilters.length || $[132] !== t) {
    t59 = selectedCategoryFilters.length > 0 ? t("gift-catalog:filterBy", {
      count: selectedCategoryFilters.length
    }) : t("gift-catalog:filterByWithoutCount");
    $[131] = selectedCategoryFilters.length;
    $[132] = t;
    $[133] = t59;
  } else {
    t59 = $[133];
  }
  let t60;
  if ($[134] !== Comp2 || $[135] !== t59) {
    t60 = <Text variant={Variants.Heading} size={Sizes.XSmall} asChild={true}><Comp2>{t59}</Comp2></Text>;
    $[134] = Comp2;
    $[135] = t59;
    $[136] = t60;
  } else {
    t60 = $[136];
  }
  let t61;
  if ($[137] !== t) {
    t61 = t("gift-catalog:clearAll");
    $[137] = t;
    $[138] = t61;
  } else {
    t61 = $[138];
  }
  let t62;
  if ($[139] !== resetAllFilters || $[140] !== t61) {
    t62 = <LinkStyle size="lg" weightOverride="normal" noPadding={true}><button type="button" onClick={resetAllFilters}>{t61}</button></LinkStyle>;
    $[139] = resetAllFilters;
    $[140] = t61;
    $[141] = t62;
  } else {
    t62 = $[141];
  }
  let t63;
  if ($[142] !== t60 || $[143] !== t62) {
    t63 = <div className="GiftCatalogCore__filtersHeader flex items-center justify-between">{t60}{t62}</div>;
    $[142] = t60;
    $[143] = t62;
    $[144] = t63;
  } else {
    t63 = $[144];
  }
  let t64;
  if ($[145] !== isCategoryOpen) {
    t64 = isCategoryOpen ? <ArkIcon iconName="chevron-up" /> : <ArkIcon iconName="chevron-down" />;
    $[145] = isCategoryOpen;
    $[146] = t64;
  } else {
    t64 = $[146];
  }
  let t65;
  if ($[147] !== isCategoryOpen) {
    t65 = () => setIsCategoryOpen(!isCategoryOpen);
    $[147] = isCategoryOpen;
    $[148] = t65;
  } else {
    t65 = $[148];
  }
  const t66 = `GiftCatalogCore__filterButton justify-between -mx-comfy-xs mt-comfy-xs mb-comfy-xs ${!isCategoryOpen ? "is-collapsed" : ""}`;
  let t67;
  if ($[149] !== t) {
    t67 = t("gift-catalog:category");
    $[149] = t;
    $[150] = t67;
  } else {
    t67 = $[150];
  }
  let t68;
  if ($[151] !== Comp3 || $[152] !== t67) {
    t68 = <Text variant={Variants.Label} size={Sizes.Large} prominence={Prominence.Potent} asChild={true}><Comp3>{t67}</Comp3></Text>;
    $[151] = Comp3;
    $[152] = t67;
    $[153] = t68;
  } else {
    t68 = $[153];
  }
  let t69;
  if ($[154] !== t65 || $[155] !== t66 || $[156] !== t68) {
    t69 = <button type="button" onClick={t65} className={t66}>{t68}</button>;
    $[154] = t65;
    $[155] = t66;
    $[156] = t68;
    $[157] = t69;
  } else {
    t69 = $[157];
  }
  let t70;
  if ($[158] !== t64 || $[159] !== t69) {
    t70 = <ButtonStyle variant="secondary" iconAfter={t64}>{t69}</ButtonStyle>;
    $[158] = t64;
    $[159] = t69;
    $[160] = t70;
  } else {
    t70 = $[160];
  }
  let t71;
  if ($[161] !== categories || $[162] !== handleActiveFilterChange || $[163] !== isCategoryOpen || $[164] !== selectedCategoryFilters) {
    t71 = isCategoryOpen && <div className="GiftCatalogCore__filterBody pb-comfy-sm"><ChipGroup chips={categories} handleSelectionChange={handleActiveFilterChange} className="gap-snug-xl" selectedValues={selectedCategoryFilters} chipSize="md" /></div>;
    $[161] = categories;
    $[162] = handleActiveFilterChange;
    $[163] = isCategoryOpen;
    $[164] = selectedCategoryFilters;
    $[165] = t71;
  } else {
    t71 = $[165];
  }
  let t72;
  if ($[166] !== t70 || $[167] !== t71) {
    t72 = <div className="GiftCatalogCore__filterSection grid">{t70}{t71}</div>;
    $[166] = t70;
    $[167] = t71;
    $[168] = t72;
  } else {
    t72 = $[168];
  }
  let t73;
  if ($[169] === Symbol.for("react.memo_cache_sentinel")) {
    t73 = <hr className="GiftCatalogCore__divider block m-none" />;
    $[169] = t73;
  } else {
    t73 = $[169];
  }
  let t74;
  if ($[170] !== isPriceOpen) {
    t74 = isPriceOpen ? <ArkIcon iconName="chevron-up" /> : <ArkIcon iconName="chevron-down" />;
    $[170] = isPriceOpen;
    $[171] = t74;
  } else {
    t74 = $[171];
  }
  let t75;
  if ($[172] !== isPriceOpen) {
    t75 = () => setIsPriceOpen(!isPriceOpen);
    $[172] = isPriceOpen;
    $[173] = t75;
  } else {
    t75 = $[173];
  }
  const t76 = `GiftCatalogCore__filterButton justify-between -mx-comfy-xs mt-comfy-xs mb-comfy-xs ${!isPriceOpen ? "is-collapsed -mb-comfy-xs" : ""}`;
  let t77;
  if ($[174] !== t) {
    t77 = t("gift-catalog:priceRange");
    $[174] = t;
    $[175] = t77;
  } else {
    t77 = $[175];
  }
  let t78;
  if ($[176] !== Comp3 || $[177] !== t77) {
    t78 = <Text variant={Variants.Label} size={Sizes.Large} prominence={Prominence.Potent} asChild={true}><Comp3>{t77}</Comp3></Text>;
    $[176] = Comp3;
    $[177] = t77;
    $[178] = t78;
  } else {
    t78 = $[178];
  }
  let t79;
  if ($[179] !== t75 || $[180] !== t76 || $[181] !== t78) {
    t79 = <button type="button" onClick={t75} className={t76}>{t78}</button>;
    $[179] = t75;
    $[180] = t76;
    $[181] = t78;
    $[182] = t79;
  } else {
    t79 = $[182];
  }
  let t80;
  if ($[183] !== t74 || $[184] !== t79) {
    t80 = <ButtonStyle variant="secondary" iconAfter={t74}>{t79}</ButtonStyle>;
    $[183] = t74;
    $[184] = t79;
    $[185] = t80;
  } else {
    t80 = $[185];
  }
  let t81;
  if ($[186] !== handlePriceChange || $[187] !== isPriceOpen || $[188] !== priceRange) {
    t81 = isPriceOpen && <div className="GiftCatalogCore__filterBody pb-comfy-sm"><Form onSubmit={_temp6} className="grid gap-snug-md"><SliderInput name="priceRange" min={generalConfig.catalogFilterMin} max={generalConfig.catalogFilterMax} step={10} prefix="$" showInputForm={true} value={priceRange} onCommit={handlePriceChange} /></Form></div>;
    $[186] = handlePriceChange;
    $[187] = isPriceOpen;
    $[188] = priceRange;
    $[189] = t81;
  } else {
    t81 = $[189];
  }
  let t82;
  if ($[190] !== t80 || $[191] !== t81) {
    t82 = <div className="GiftCatalogCore__filterSection grid">{t80}{t81}</div>;
    $[190] = t80;
    $[191] = t81;
    $[192] = t82;
  } else {
    t82 = $[192];
  }
  let t83;
  if ($[193] !== t58 || $[194] !== t63 || $[195] !== t72 || $[196] !== t82) {
    t83 = <aside className={t58}>{t63}{t72}{t73}{t82}</aside>;
    $[193] = t58;
    $[194] = t63;
    $[195] = t72;
    $[196] = t82;
    $[197] = t83;
  } else {
    t83 = $[197];
  }
  const t84 = !isMobileFiltersOpen;
  let t85;
  if ($[198] !== t84) {
    t85 = cn("GiftCatalogCore__main grid gap-comfy-lg", {
      "pt-comfy-xs": t84
    });
    $[198] = t84;
    $[199] = t85;
  } else {
    t85 = $[199];
  }
  const t86 = (selectedCategoryFilters.length > 0 || isPriceFiltered) && <div className="flex flex-wrap gap-snug-xl">{selectedCategoryFilters.length > 0 && <ChipGroup chips={selectedCategoryFilters.map(key => ({
      name: categoryLabels[key] || key,
      value: key,
      isSelected: false
    }))} selectedValues={selectedCategoryFilters} handleSelectionChange={handleActiveFilterChange} removable={true} onRemoveChip={value => handleActiveFilterChange(selectedCategoryFilters.filter(v => v !== value))} className="gap-snug-xl" chipSize="sm" />}{isPriceFiltered && <ChipGroup chips={[{
      name: t("gift-catalog:priceRangeChip", {
        min: giftCatalogFilters.priceMin ?? generalConfig.catalogFilterMin,
        max: giftCatalogFilters.priceMax ?? generalConfig.catalogFilterMax
      }),
      value: "price-range",
      isSelected: false
    }]} selectedValues={[]} handleSelectionChange={resetPriceFilter} removable={true} onRemoveChip={resetPriceFilter} className="gap-snug-xl" chipSize="sm" />}</div>;
  let t87;
  if ($[200] !== Comp2 || $[201] !== allResults || $[202] !== currentPage || $[203] !== isLoading || $[204] !== searchResults?.totalCount || $[205] !== seoLevel || $[206] !== t) {
    t87 = isLoading && currentPage === 1 ? <GiftCatalogCoreCardsSkeleton itemCount={generalConfig.catalogPerPage} /> : <><Text variant={Variants.Label} size={Sizes.XLarge} asChild={true}><Comp2>{t("gift-catalog:showingGifts", {
            count: allResults.length,
            countAllGifts: searchResults?.totalCount
          })}</Comp2></Text><div className="grid gap-comfy-sm @md:gap-comfy-lg grid-cols-1 @sm:grid-cols-2 @md:grid-cols-3">{allResults && <TrackViewFundItemList listId="gift_catalog" listName="Gift Catalog" items={allResults.map(_temp7)} isCatalog={true} />}{allResults.map((gift, index) => <GiftCatalogCard seoLevel={seoLevel + 2} key={gift.path || `gift-${index}`} {...gift} variant="grid" onClick={() => trackSelectFundItem("giftCatalog", "Gift Catalog", gift.fund as FundProps, true)} />)}</div></>;
    $[200] = Comp2;
    $[201] = allResults;
    $[202] = currentPage;
    $[203] = isLoading;
    $[204] = searchResults?.totalCount;
    $[205] = seoLevel;
    $[206] = t;
    $[207] = t87;
  } else {
    t87 = $[207];
  }
  let t88;
  if ($[208] !== currentPage || $[209] !== handleLoadMore || $[210] !== hasMore || $[211] !== isLoading || $[212] !== t) {
    t88 = Boolean(hasMore) && <div className="flex justify-center mt-comfy-lg"><ButtonStyle variant="secondary" size="lg"><button type="button" onClick={handleLoadMore} disabled={isLoading}>{isLoading && currentPage > 1 ? t("gift-catalog:loading") : t("gift-catalog:loadMore")}</button></ButtonStyle></div>;
    $[208] = currentPage;
    $[209] = handleLoadMore;
    $[210] = hasMore;
    $[211] = isLoading;
    $[212] = t;
    $[213] = t88;
  } else {
    t88 = $[213];
  }
  let t89;
  if ($[214] !== t85 || $[215] !== t86 || $[216] !== t87 || $[217] !== t88) {
    t89 = <div className={t85}>{t86}{t87}{t88}</div>;
    $[214] = t85;
    $[215] = t86;
    $[216] = t87;
    $[217] = t88;
    $[218] = t89;
  } else {
    t89 = $[218];
  }
  let t90;
  if ($[219] !== t83 || $[220] !== t89) {
    t90 = <div className={t56}>{t83}{t89}</div>;
    $[219] = t83;
    $[220] = t89;
    $[221] = t90;
  } else {
    t90 = $[221];
  }
  let t91;
  if ($[222] !== t55 || $[223] !== t90) {
    t91 = <section className={t37}><div className={t38}>{t55}{t90}</div></section>;
    $[222] = t55;
    $[223] = t90;
    $[224] = t91;
  } else {
    t91 = $[224];
  }
  return t91;
};
export const GiftCatalogCore: React.FC<BlockProps> = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <GiftCatalogCoreSkeleton itemCount={generalConfig.catalogPerPage} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <Suspense fallback={t0}><QueryClientProvider client={queryClient}><InternalGiftCatalogCore {...props} /></QueryClientProvider></Suspense>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
function _temp(sortBy) {
  switch (sortBy) {
    case GiftCatalogSortEnum.enum.mostNeeded:
      {
        return {
          should: "mostNeeded"
        };
      }
    case GiftCatalogSortEnum.enum.priceLowToHigh:
      {
        return {
          sort: "fund.catalogAmount" as const,
          sortDir: "asc" as const
        };
      }
    case GiftCatalogSortEnum.enum.priceHighToLow:
      {
        return {
          sort: "fund.catalogAmount" as const,
          sortDir: "desc" as const
        };
      }
    case GiftCatalogSortEnum.enum.alphabeticalAZ:
      {
        return {
          sort: "pageTitle.keyword" as const,
          sortDir: "asc" as const
        };
      }
    case GiftCatalogSortEnum.enum.alphabeticalZA:
      {
        return {
          sort: "pageTitle.keyword" as const,
          sortDir: "desc" as const
        };
      }
    default:
      {
        return {
          sort: "pageTitle.keyword" as const,
          sortDir: "asc" as const
        };
      }
  }
}
function _temp2(facet) {
  return facet?.field === "taxonomy.fundCategory.keyword";
}
function _temp3(a, b) {
  const categoryA = a.name.toLowerCase();
  const categoryB = b.name.toLowerCase();
  if (categoryA < categoryB) {
    return -1;
  }
  if (categoryA > categoryB) {
    return 1;
  }
  return 0;
}
function _temp4(prev_3) {
  return prev_3 + 1;
}
function _temp5(prev_4) {
  return {
    ...prev_4,
    priceMin: null,
    priceMax: null
  };
}
function _temp6() {}
function _temp7(result) {
  return result.fund as FundProps;
}